// v3.4.510 — الخطوة الثانية لدخول الموظفين (بعد Google): رمز تطبيق المصادقة (Google Authenticator) أو رمز واتساب من الرقم الموحد أو رمز احتياطي،
// ومن لم يربط التطبيق بعد (والسياسة إلزامية) يربطه هنا لأول مرة. تُعرض داخل بطاقة صفحة الدخول.
import React, { useEffect, useRef, useState } from "react";
import { auth, twoFactor } from "../services/api";

const box = { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", margin: "12px 0", fontSize: 13, color: "#334155", lineHeight: 1.9, textAlign: "right" };
const btn = (bg, disabled) => ({ width: "100%", padding: "11px 16px", border: "none", borderRadius: 8, background: disabled ? "#cbd5e1" : bg, color: "#fff", fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: disabled ? "default" : "pointer" });
const link = { border: "none", background: "none", color: "#2563eb", fontFamily: "inherit", fontSize: 12.5, cursor: "pointer", padding: "4px 6px", fontWeight: 600 };
const errBox = { background: "#fee2e2", color: "#991b1b", padding: "9px 12px", borderRadius: 8, margin: "10px 0", fontSize: 13, fontWeight: 500, lineHeight: 1.7 };

export function CodeInput({ value, onChange, onEnter, length = 6, text = false, autoFocus = true }) {
  return (
    <input value={value} autoFocus={autoFocus} dir="ltr" inputMode={text ? "text" : "numeric"} autoComplete="one-time-code" maxLength={text ? 9 : length}
      placeholder={text ? "xxxx-xxxx" : "••••••"}
      onChange={(e) => onChange(text ? e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") : e.target.value.replace(/[^0-9٠-٩]/g, "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)).slice(0, length))}
      onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
      style={{ width: "100%", boxSizing: "border-box", padding: "12px 10px", border: "2px solid #cbd5e1", borderRadius: 10, fontSize: 24, fontWeight: 700, letterSpacing: text ? 3 : 10, textAlign: "center", fontFamily: "monospace", outline: "none" }} />
  );
}

// عرض الرموز الاحتياطية مرة واحدة (بعد ربط التطبيق أو توليد رموز جديدة)
export function BackupCodes({ codes }) {
  const copy = () => { try { navigator.clipboard.writeText(codes.join("\n")); } catch (_) { /* النسخ غير متاح */ } };
  return (
    <div style={{ ...box, background: "#fffbeb", borderColor: "#fde68a" }}>
      <div style={{ fontWeight: 800, color: "#92400e", marginBottom: 4 }}>🔑 الرموز الاحتياطية — احفظها الآن (لن تُعرض مرة أخرى)</div>
      <div style={{ fontSize: 12, color: "#92400e" }}>كل رمز يُستخدم مرة واحدة للدخول إذا لم يكن جوالك معك.</div>
      <div dir="ltr" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, margin: "10px 0", fontFamily: "monospace", fontSize: 15, fontWeight: 700, textAlign: "center", color: "#1e293b" }}>
        {codes.map((c) => <div key={c} style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 0" }}>{c}</div>)}
      </div>
      <button type="button" onClick={copy} style={{ ...link, color: "#92400e" }}>📋 نسخ الرموز</button>
    </div>
  );
}

// تعليمات الربط + رمز QR + المفتاح اليدوي
export function EnrollGuide({ data }) {
  return (
    <div style={box}>
      <div><b>1.</b> ثبّت تطبيق <b>Google Authenticator</b> على جوالك (أو Microsoft Authenticator).</div>
      <div><b>2.</b> من التطبيق اضغط <b>+</b> ثم «مسح رمز QR» وصوّر الرمز:</div>
      <div style={{ textAlign: "center", margin: "8px 0" }}><img src={data.qr} alt="QR" width={190} height={190} style={{ border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff" }} /></div>
      <div style={{ fontSize: 12, color: "#64748b" }}>أو أدخل المفتاح يدويًا (الحساب: <span dir="ltr">{data.account}</span>):</div>
      <div dir="ltr" style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, textAlign: "center", background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 6, padding: "6px 4px", margin: "4px 0 8px", wordBreak: "break-all", userSelect: "all" }}>{data.secret}</div>
      <div><b>3.</b> أدخل الرمز المكوّن من 6 أرقام الظاهر في التطبيق:</div>
    </div>
  );
}

export default function TwoFactorStep({ mfa }) {
  const [method, setMethod] = useState(mfa.totp ? "totp" : (mfa.whatsapp ? "whatsapp" : "totp"));
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(mfa.locked ? "محاولات خاطئة كثيرة — أعد المحاولة بعد 15 دقيقة" : (mfa.error ? "تعذّر تحميل حالة التحقق — أعد تحميل الصفحة" : ""));
  const [sent, setSent] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [backup, setBackup] = useState(null);
  const started = useRef(false);

  const onError = (e, fallback) => {
    const c = e.response?.data?.code;
    if (c === "no_pending" || c === "mfa_timeout") { window.location.replace("/login?error=mfa_timeout"); return; }
    setErr(e.response?.data?.error || fallback);
  };
  const enter = () => window.location.replace("/");
  const otherAccount = async () => { try { await auth.logout(); } catch (_) { /* الجلسة منتهية أصلًا */ } window.location.replace("/login"); };

  // ربط التطبيق لأول مرة
  useEffect(() => {
    if (!mfa.enroll || started.current) return;
    started.current = true;
    twoFactor.enrollStart().then((r) => setEnroll(r.data)).catch((e) => {
      const c = e.response?.data?.code;
      if (c === "no_pending" || c === "mfa_timeout") window.location.replace("/login?error=mfa_timeout");
      else setErr(e.response?.data?.error || "تعذّر بدء الربط — أعد تحميل الصفحة");
    });
  }, [mfa.enroll]);

  const confirmEnroll = async () => {
    if (code.length !== 6 || busy) return;
    setBusy(true); setErr("");
    try { const r = await twoFactor.enrollConfirm(code); setBackup(r.data.backup_codes || []); }
    catch (e) { onError(e, "الرمز غير صحيح"); setCode(""); }
    finally { setBusy(false); }
  };
  const verify = async (value) => {
    const v = value ?? code;
    if (busy || (method === "backup" ? v.replace(/-/g, "").length !== 8 : v.length !== 6)) return;
    setBusy(true); setErr("");
    try { await twoFactor.loginVerify(method, v); enter(); }
    catch (e) { onError(e, "الرمز غير صحيح"); setCode(""); setBusy(false); }
  };
  const sendWa = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try { const r = await twoFactor.loginSend(); setSent(r.data); }
    catch (e) { onError(e, "تعذّر إرسال الرمز"); }
    finally { setBusy(false); }
  };
  const pick = (m) => { setMethod(m); setCode(""); setErr(""); };

  const head = (
    <>
      <h1 style={{ fontSize: 20 }}>🔐 التحقق بخطوتين</h1>
      <p style={{ marginBottom: 8 }}><span dir="ltr">{mfa.email}</span></p>
    </>
  );

  if (backup) return (
    <div>
      {head}
      <div style={{ ...box, background: "#ecfdf5", borderColor: "#a7f3d0", color: "#065f46", fontWeight: 700, textAlign: "center" }}>✓ تم ربط تطبيق المصادقة بحسابك</div>
      <BackupCodes codes={backup} />
      <button type="button" onClick={enter} style={btn("#16a34a")}>حفظت الرموز — دخول النظام</button>
    </div>
  );

  if (mfa.enroll) return (
    <div>
      {head}
      <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.8 }}>لحماية حسابك، الدخول يتطلب رمزًا من تطبيق المصادقة على جوالك. اربط التطبيق الآن (مرة واحدة):</div>
      {err && <div style={errBox}>{err}</div>}
      {enroll ? (
        <>
          <EnrollGuide data={enroll} />
          <CodeInput value={code} onChange={setCode} onEnter={confirmEnroll} />
          <div style={{ height: 10 }} />
          <button type="button" onClick={confirmEnroll} disabled={busy || code.length !== 6} style={btn("#2563eb", busy || code.length !== 6)}>{busy ? "جارٍ التحقق…" : "تأكيد وربط"}</button>
        </>
      ) : !err && <div style={{ padding: 20, color: "#94a3b8", fontSize: 13 }}>جارٍ التحضير…</div>}
      <div style={{ marginTop: 10 }}><button type="button" onClick={otherAccount} style={{ ...link, color: "#64748b" }}>الدخول بحساب آخر</button></div>
    </div>
  );

  return (
    <div>
      {head}
      {err && <div style={errBox}>{err}</div>}
      {method === "totp" && <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.8, marginBottom: 10 }}>أدخل الرمز المكوّن من 6 أرقام من تطبيق <b>Google Authenticator</b></div>}
      {method === "backup" && <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.8, marginBottom: 10 }}>أدخل أحد رموزك الاحتياطية (يُستخدم مرة واحدة)</div>}
      {method === "whatsapp" && (
        <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.8, marginBottom: 10 }}>
          {sent ? <>أُرسل الرمز على واتساب إلى <b dir="ltr">{sent.to}</b> من الرقم الموحد — صالح {sent.minutes} دقائق</> : <>يُرسل رمز على واتساب إلى جوالك <b dir="ltr">{mfa.whatsapp}</b> من الرقم الموحد 9200</>}
        </div>
      )}
      {method === "whatsapp" && !sent ? (
        <button type="button" onClick={sendWa} disabled={busy} style={btn("#16a34a", busy)}>{busy ? "جارٍ الإرسال…" : "إرسال الرمز على واتساب"}</button>
      ) : (
        <>
          <CodeInput key={method} value={code} text={method === "backup"} onEnter={() => verify()}
            onChange={(v) => { setCode(v); if (method !== "backup" && v.length === 6) verify(v); }} />
          <div style={{ height: 10 }} />
          <button type="button" onClick={() => verify()} disabled={busy} style={btn("#2563eb", busy)}>{busy ? "جارٍ التحقق…" : "دخول"}</button>
          {method === "whatsapp" && <div style={{ marginTop: 6 }}><button type="button" onClick={sendWa} disabled={busy} style={link}>إعادة إرسال الرمز</button></div>}
        </>
      )}
      <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 4 }}>
        {method !== "totp" && mfa.totp && <button type="button" onClick={() => pick("totp")} style={link}>تطبيق المصادقة</button>}
        {method !== "whatsapp" && mfa.whatsapp && <button type="button" onClick={() => pick("whatsapp")} style={link}>رمز واتساب</button>}
        {method !== "backup" && mfa.backup && <button type="button" onClick={() => pick("backup")} style={link}>رمز احتياطي</button>}
        <button type="button" onClick={otherAccount} style={{ ...link, color: "#64748b" }}>الدخول بحساب آخر</button>
      </div>
      <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 8, lineHeight: 1.7 }}>فقدت جوالك؟ اطلب من مدير النظام «إعادة ضبط» التحقق لحسابك.</div>
    </div>
  );
}
